/**
 * A record's place on the 5-step timeline (Filed → Introduced → Committee → Council → Final), from the Clerk's actions
 * (records matter_actions). Shared by the meeting snapshot builder (worker/src/meeting-assemble.ts) and the meeting
 * page's Legislation tab (components/meeting/LegislationTab.tsx, ProgressTracker).
 *
 * The stage is the step a record is at or waiting for; the steps before it are done:
 *   Filed (0)       nothing yet beyond the Clerk's filing
 *   Introduced (1)  filed, waiting to be introduced
 *   Committee (2)   introduced and referred: waiting for, or held in, committee
 *   Council (3)     the committee reported it (or the Council took it up directly): waiting for a Council vote
 *   Final (4)       final action: passed, adopted, signed, or failed, vetoed, withdrawn, placed on file
 * The latest action that says something about the stage decides it (a re-referral sends a record back to committee).
 */
import type { Legislation } from "../types";

export type StageIndex = Legislation["stage"];
export const STAGE_LABELS = ["Filed", "Introduced", "Committee", "Council", "Final"] as const;

/** One Clerk action (matter_actions: action_date, action_name, action_by). */
export interface StageAction { date?: string | null; action?: string | null; body?: string | null }
export type FinalOutcome = "passed" | "failed" | "withdrawn";

const COUNCIL = /city council/i;
const COMMITTEE = /committee/i;

/** What one action says: the stage after it (null: nothing, e.g. co-sponsor changes), and the outcome of a final action. */
export function stageOfAction(a: StageAction): { stage: StageIndex; outcome?: FinalOutcome } | null {
  const name = (a.action || "").trim(), by = a.body || "";
  if (!name || /co-?sponsor/i.test(name)) return null;
  if (/withdrawn|placed on file|repealed/i.test(name)) return { stage: 4, outcome: "withdrawn" };
  if (/failed|vetoed|tabled|do not pass/i.test(name) && !COMMITTEE.test(by)) return { stage: 4, outcome: "failed" };
  if (/signed by mayor|special pamphlet/i.test(name)) return { stage: 4, outcome: "passed" };
  const atCommittee = COMMITTEE.test(by) && !COUNCIL.test(by);
  if (atCommittee || (!COUNCIL.test(by) && /recommended|held in committee|in committee/i.test(name))) {
    // A committee's report sends the record to the Council; anything else keeps it in committee.
    return /recommended|discharged|report/i.test(name) ? { stage: 3 } : { stage: 2 };
  }
  if (/^(passed|adopted|approved|passed as substitute|concurred)/i.test(name)) return { stage: 4, outcome: "passed" };
  if (/introduc|referred|re-referred/i.test(name)) return { stage: 2 };
  if (/discharged|deferred|published|immediate consideration|rules suspended|considered|amended|recommended/i.test(name)) return { stage: 3 };
  if (/filed/i.test(name) || /clerk/i.test(by)) return { stage: 1 };
  return null;
}

/** Fallback from the Clerk's status code alone ("40-In Committee"), as before actions were read. */
export function stageOfStatus(status: string | null | undefined): StageIndex {
  const s = status || "";
  return /^90/.test(s) ? 4 : /^5/.test(s) ? 3 : /^4/.test(s) ? 2 : /^3/.test(s) ? 1 : 0;
}

const day10 = (d: string | null | undefined) => String(d || "").slice(0, 10);

/** The latest stage the actions say anything about (any order; a final action stays final), or null. */
function latestStage(actions: StageAction[]): { stage: StageIndex; outcome?: FinalOutcome } | null {
  const sorted = actions.map((a, i) => ({ a, i, k: String(a.date || "") })).sort((x, y) => (x.k < y.k ? -1 : x.k > y.k ? 1 : x.i - y.i));
  let out: { stage: StageIndex; outcome?: FinalOutcome } | null = null;
  for (const { a } of sorted) {
    const s = stageOfAction(a);
    if (!s || (out?.stage === 4 && s.stage < 4)) continue;   // later clerical entries don't reopen a final action
    out = s;
  }
  return out;
}

/** The record's stage now, from its actions; with none that say anything, from the status code. */
export function computeStage(actions: StageAction[], status?: string | null): { stage: StageIndex; outcome?: FinalOutcome } {
  return latestStage(actions) ?? { stage: stageOfStatus(status) };
}

/** The record's stage at the end of a day (YYYY-MM-DD; that day's actions count), or null when no action by then says. */
export const stageAsOf = (actions: StageAction[], day: string): StageIndex | null =>
  latestStage(actions.filter((a) => day10(a.date) !== "" && day10(a.date) <= day10(day)))?.stage ?? null;

/** Moved since the meeting: its stage now is past the stage it had at the end of the meeting day. */
export const movedSinceMeeting = (b: Pick<Legislation, "stage" | "stageAtMeeting" | "latestAction">, meetingDate: string) =>
  b.stageAtMeeting != null ? b.stage > b.stageAtMeeting : day10(b.latestAction.date) > day10(meetingDate);

export type IntroFilter = "all" | "key" | "moved";

/** The Legislation tab's filter (All · Key legislation · Moved since meeting): its counts and the rows it shows. */
export function introducedFilter(bills: Legislation[], meetingDate: string, filter: IntroFilter) {
  const moved = (b: Legislation) => movedSinceMeeting(b, meetingDate);
  return {
    counts: { all: bills.length, key: bills.filter((b) => b.isKey).length, moved: bills.filter(moved).length },
    shown: bills.filter((b) => filter === "all" || (filter === "key" ? !!b.isKey : moved(b))),
  };
}

/** The action as the row writes it: sentence case, with Committee / Council / City / Mayor kept ("Held in Committee", "Recommended to pass"). */
export const actionLabel = (action: string) =>
  action.trim().toLowerCase().replace(/^./, (c) => c.toUpperCase()).replace(/\b(committee|council|city|mayor)\b/g, (w) => w[0].toUpperCase() + w.slice(1));

/** The body as the row writes it: "Committee on License and Consumer Protection" → "License and Consumer Protection". */
export const bodyLabel = (body: string) => body.replace(/^Committee on (the )?/i, "").trim();

/** A sponsor's name from the Clerk ("Johnson, Brandon") in reading order ("Brandon Johnson"). */
export const readingName = (name: string) => {
  const m = /^([^,]+),\s*([^,]+?)(?:,\s*(Jr\.?|Sr\.?|II|III|IV))?$/.exec(name.trim());
  return m ? `${m[2]} ${m[1]}${m[3] ? ` ${m[3]}` : ""}` : name.trim();
};
